import React, { useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Shield, Eye, Lock, FileText, CheckCircle2 } from 'lucide-react';

export function PrivacyPolicyPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={`pt-28 pb-20 transition-colors duration-500 ${isDark ? 'bg-dark-bg text-gray-300' : 'bg-bg-light text-gray-700'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/15 border border-accent/20 mb-4">
            <Shield className="w-4 h-4 text-accent" />
            <span className="text-xs uppercase font-poppins tracking-wider font-semibold text-accent animate-pulse">LEGAL PROTECTION</span>
          </div>
          <h1 className={`text-3xl sm:text-4xl lg:text-[44px] font-poppins font-bold tracking-tight mb-4 ${isDark ? 'text-white' : 'text-primary'}`}>
            Privacy Policy
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Last Updated: May 2026
          </p>
        </div>

        {/* Content Box */}
        <div className={`rounded-3xl p-6 sm:p-10 border transition-all duration-300 ${
          isDark 
            ? 'bg-[#111827]/80 border-white/[0.06] shadow-xl' 
            : 'bg-white border-black/[0.04] shadow-xl shadow-slate-100/50'
        }`}>
          
          <section className="mb-8">
            <h2 className={`text-xl sm:text-2xl font-poppins font-bold mb-4 flex items-center gap-2.5 ${isDark ? 'text-white' : 'text-primary'}`}>
              <Eye className="w-5 h-5 text-accent" />
              1. Introduction
            </h2>
            <p className="text-sm leading-relaxed mb-4">
              At <strong>Aura Spaces</strong> (operating as "Aura Spaces by Harmony Home Designs"), we are committed to protecting the privacy and security of our clients, visitors, and users. This Privacy Policy details how we collect, use, process, and disclose your information across our website and during our design consultation services.
            </p>
            <p className="text-sm leading-relaxed">
              By visiting our website or utilizing our design services, you consent to the data collection and usage practices described in this document.
            </p>
          </section>

          <hr className={`my-8 ${isDark ? 'border-gray-800' : 'border-gray-150/80'}`} />

          <section className="mb-8">
            <h2 className={`text-xl sm:text-2xl font-poppins font-bold mb-4 flex items-center gap-2.5 ${isDark ? 'text-white' : 'text-primary'}`}>
              <FileText className="w-5 h-5 text-accent" />
              2. Information We Collect
            </h2>
            <p className="text-sm leading-relaxed mb-4">
              We collect information directly from you when you interact with our website, request estimations, fill out consultation forms, or communicate with our project managers.
            </p>
            <ul className="space-y-3 pl-1 mb-4">
              <li className="flex items-start gap-2.5 text-sm leading-snug">
                <CheckCircle2 className="w-4.5 h-4.5 text-accent shrink-0 mt-0.5" />
                <span><strong>Personal Contact Details:</strong> Name, email address, phone number, and physical property address.</span>
              </li>
              <li className="flex items-start gap-2.5 text-sm leading-snug">
                <CheckCircle2 className="w-4.5 h-4.5 text-accent shrink-0 mt-0.5" />
                <span><strong>Project Specifications:</strong> Floor plans, room counts (e.g. 2BHK/3BHK), budget requirements, design preferences, and service interests.</span>
              </li>
              <li className="flex items-start gap-2.5 text-sm leading-snug">
                <CheckCircle2 className="w-4.5 h-4.5 text-accent shrink-0 mt-0.5" />
                <span><strong>Usage Data:</strong> Dynamic technical logs, IP address, device type, browser information, and visited page history.</span>
              </li>
            </ul>
          </section>

          <hr className={`my-8 ${isDark ? 'border-gray-800' : 'border-gray-150/80'}`} />

          <section className="mb-8">
            <h2 className={`text-xl sm:text-2xl font-poppins font-bold mb-4 flex items-center gap-2.5 ${isDark ? 'text-white' : 'text-primary'}`}>
              <Lock className="w-5 h-5 text-accent" />
              3. How We Use Your Information
            </h2>
            <p className="text-sm leading-relaxed mb-4">
              Aura Spaces processes your data to deliver top-tier premium experiences. Specifically, we use your collected details to:
            </p>
            <ul className="space-y-3 pl-1">
              <li className="flex items-start gap-2.5 text-sm leading-snug">
                <CheckCircle2 className="w-4.5 h-4.5 text-accent shrink-0 mt-0.5" />
                <span>Schedule and manage your free 3D design consultation appointments.</span>
              </li>
              <li className="flex items-start gap-2.5 text-sm leading-snug">
                <CheckCircle2 className="w-4.5 h-4.5 text-accent shrink-0 mt-0.5" />
                <span>Calculate rough modular kitchen, wardrobe, and complete interior cost estimations.</span>
              </li>
              <li className="flex items-start gap-2.5 text-sm leading-snug">
                <CheckCircle2 className="w-4.5 h-4.5 text-accent shrink-0 mt-0.5" />
                <span>Communicate project manufacturing status, execution stages, and on-site handover updates.</span>
              </li>
              <li className="flex items-start gap-2.5 text-sm leading-snug">
                <CheckCircle2 className="w-4.5 h-4.5 text-accent shrink-0 mt-0.5" />
                <span>Enhance our website's responsive UI metrics, performance, and general user experience.</span>
              </li>
            </ul>
          </section>

          <hr className={`my-8 ${isDark ? 'border-gray-800' : 'border-gray-150/80'}`} />

          <section className="mb-8">
            <h2 className={`text-xl sm:text-2xl font-poppins font-bold mb-4 flex items-center gap-2.5 ${isDark ? 'text-white' : 'text-primary'}`}>
              <Shield className="w-5 h-5 text-accent" />
              4. Data Splicing & Protection
            </h2>
            <p className="text-sm leading-relaxed mb-4">
              We employ robust administrative, technical, and physical security measures to shield your data from unauthorized access, modification, exposure, or destruction. 
            </p>
            <p className="text-sm leading-relaxed">
              <strong>Zero Marketing Sales:</strong> We do NOT sell, rent, or trade your personal contact details with third-party companies for their analytical or marketing purposes. Your contact numbers and details are purely processed by Aura Spaces project managers and in-house carpenters.
            </p>
          </section>

          <hr className={`my-8 ${isDark ? 'border-gray-800' : 'border-gray-150/80'}`} />

          <section className="mb-8">
            <h2 className={`text-xl sm:text-2xl font-poppins font-bold mb-4 ${isDark ? 'text-white' : 'text-primary'}`}>
              5. Cookies and Technical Tracking
            </h2>
            <p className="text-sm leading-relaxed">
              Our website uses basic technical cookies to save your user preferences (such as light/dark mode settings) and analyze traffic flows using low-impact anonymous counters. You can configure your browser to reject cookies, though some interactive elements might degrade in performance.
            </p>
          </section>

          <hr className={`my-8 ${isDark ? 'border-gray-800' : 'border-gray-150/80'}`} />

          <section>
            <h2 className={`text-xl sm:text-2xl font-poppins font-bold mb-4 ${isDark ? 'text-white' : 'text-primary'}`}>
              6. Legal Contact & Queries
            </h2>
            <p className="text-sm leading-relaxed mb-4">
              If you have any questions about this Privacy Policy, wish to update your files, or request data deletion, please contact us immediately:
            </p>
            <div className={`p-5 rounded-2xl border text-sm leading-relaxed ${isDark ? 'bg-slate-900/60 border-gray-850' : 'bg-slate-50 border-slate-200/50'}`}>
              <strong className="block mb-1">Aura Spaces Legal Desk</strong>
              Email: <a href="mailto:operations@auraspaces.net" className="text-accent font-bold hover:underline">operations@auraspaces.net</a><br />
              Phone: <span className="font-bold">+91 9989697942</span><br />
              Design Studio: Hyderabad & Vijayawada, India.
            </div>
          </section>

        </div>

      </div>
    </div>
  );
}
